import { useCallback, useEffect, useRef } from 'react';
import { Alert, Button, LinearProgress } from '@mui/material';
import useTeamsSetupStore, { TeamsAuthRuntime, TeamsContext } from '../../store/teamsSetupStore';
import SetBoardDialog, { SelectedBoardResult } from './SetBoardDialog';

interface TeamsAuthenticationProps {
  teams: TeamsAuthRuntime | null;
  context: TeamsContext | null;
  isReady: boolean;
  initError: string | null;
  isSetBoardSectionVisible: boolean;
  onAuthenticated: () => void;
  onBoardSelected: (result: SelectedBoardResult) => void;
}

const TeamsAuthentication = ({
  teams,
  context,
  isReady,
  initError,
  isSetBoardSectionVisible,
  onAuthenticated,
  onBoardSelected,
}: TeamsAuthenticationProps) => {
  const apiMe = useTeamsSetupStore((state) => state.apiMe);
  const loginError = useTeamsSetupStore((state) => state.loginError);
  const isLoggingIn = useTeamsSetupStore((state) => state.isLoggingIn);
  const appAuthToken = useTeamsSetupStore((state) => state.appAuthToken);
  const defaultBoardTitle = useTeamsSetupStore((state) => state.defaultBoardTitle);
  const setLoginError = useTeamsSetupStore((state) => state.setLoginError);
  const resetAuthState = useTeamsSetupStore((state) => state.resetAuthState);
  const loginWithTeams = useTeamsSetupStore((state) => state.loginWithTeams);
  const hasAttemptedAutoLoginRef = useRef(false);

  const isAuthenticated = Boolean(apiMe && appAuthToken);

  useEffect(() => {
    resetAuthState();
  }, [resetAuthState]);

  const handleDialogLogin = useCallback(async () => {
    if (!teams || !context) {
      setLoginError('Teams context is not ready yet.');
      return;
    }

    await loginWithTeams({ teams, context });
  }, [context, loginWithTeams, setLoginError, teams]);

  useEffect(() => {
    if (!isReady || !context || hasAttemptedAutoLoginRef.current) return;

    hasAttemptedAutoLoginRef.current = true;
    void handleDialogLogin();
  }, [context, handleDialogLogin, isReady]);

  useEffect(() => {
    if (!isAuthenticated) return;
    onAuthenticated();
  }, [isAuthenticated, onAuthenticated]);

  return (
    <>
      {!initError && isReady && isSetBoardSectionVisible ? (
        <SetBoardDialog
          appAuthToken={appAuthToken}
          defaultTitle={defaultBoardTitle}
          loginError={loginError}
          isLoggedIn={isAuthenticated}
          isLoggingIn={isLoggingIn}
          onLogin={handleDialogLogin}
          onBoardSelected={onBoardSelected}
        />
      ) : null}

      {!initError && isReady && !isAuthenticated && isLoggingIn ? <LinearProgress /> : null}
      {!initError && isReady && !isAuthenticated && loginError ? (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void handleDialogLogin()}>
              Retry login
            </Button>
          }
        >
          {loginError}
        </Alert>
      ) : null}
    </>
  );
};

export default TeamsAuthentication;
